import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Filter, TrendingUp, Users, Briefcase, Target } from 'lucide-react';
import {
  getEnrichedJobs,
  getTop25PercentJobs,
  getDayWiseAnalytics,
  generateAnalystReport,
  updateJobSourcingData,
  loadPersistedJobs
} from '../utils/adaJobEnrichment';

const AnalystDailyReport = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA')); // YYYY-MM-DD format
  const [top25Jobs, setTop25Jobs] = useState([]);
  const [dayAnalytics, setDayAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    minAdaScore: 0,
    source: ''
  });
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [sortBy, setSortBy] = useState('relevance'); // relevance, ada, company

  useEffect(() => {
    // Load persisted jobs on first mount
    initializeData();
  }, []);

  useEffect(() => {
    // Load report data when date or filters change
    loadReportData();
  }, [selectedDate, filters]);

  const initializeData = async () => {
    try {
      await loadPersistedJobs();
      console.log('✅ Persisted jobs loaded successfully');
    } catch (error) {
      console.warn('⚠️ Could not load persisted jobs:', error.message);
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      // Get top 25% jobs for the selected date
      const allJobs = getEnrichedJobs({ 
        date: selectedDate,
        industry: filters.industry || undefined,
        adaScoreMin: filters.minAdaScore || undefined
      });
      
      const top25 = allJobs.slice(0, Math.ceil(allJobs.length * 0.25) || 0);
      setTop25Jobs(top25);
      
      // Get day-wise analytics
      const analytics = getDayWiseAnalytics(selectedDate);
      setDayAnalytics(analytics);
      
      console.log('📊 Loaded analyst report:', top25.length, 'jobs');
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    try {
      const csv = generateAnalystReport(selectedDate);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analyst-report-top-25-${selectedDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleUpdateSourcing = (jobId, data) => {
    updateJobSourcingData(jobId, data);
    loadReportData();
  };

  const sortedJobs = [...top25Jobs].sort((a, b) => {
    if (sortBy === 'relevance') {
      return (b.analytics.relevanceScore || 0) - (a.analytics.relevanceScore || 0);
    } else if (sortBy === 'ada') {
      return (b.ada.complianceScore || 0) - (a.ada.complianceScore || 0);
    } else if (sortBy === 'company') {
      return (a.company.name || '').localeCompare(b.company.name || '');
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl font-bold text-gray-900">Analyst Daily Report</h1>
          </div>
          <p className="text-gray-600">Top 25% high-quality ADA job opportunities for candidate sourcing</p>
        </div>

        {/* Date & Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
              <input
                type="text"
                placeholder="Filter by industry"
                value={filters.industry}
                onChange={(e) => setFilters({...filters, industry: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min ADA Score</label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 70"
                value={filters.minAdaScore}
                onChange={(e) => setFilters({...filters, minAdaScore: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="relevance">Relevance Score</option>
                <option value="ada">ADA Score</option>
                <option value="company">Company Name</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleExportReport}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Summary */}
        {dayAnalytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Jobs</p>
                  <p className="text-3xl font-bold text-gray-900">{dayAnalytics.totalJobs}</p>
                </div>
                <Briefcase className="w-8 h-8 text-blue-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Top 25%</p>
                  <p className="text-3xl font-bold text-purple-900">{dayAnalytics.top25Count}</p>
                </div>
                <Target className="w-8 h-8 text-purple-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Avg ADA Score</p>
                  <p className="text-3xl font-bold text-green-900">{dayAnalytics.averageAdaScore.toFixed(1)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Qualified Candidates</p>
                  <p className="text-3xl font-bold text-orange-900">{dayAnalytics.totalQualifiedCandidates}</p>
                </div>
                <Users className="w-8 h-8 text-orange-600 opacity-20" />
              </div>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Rank</th>
                  <th className="px-4 py-3 text-left">Job Title</th>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Industry</th>
                  <th className="px-4 py-3 text-center">ADA Score</th>
                  <th className="px-4 py-3 text-center">Relevance</th>
                  <th className="px-4 py-3 text-center">Salary Range</th>
                  <th className="px-4 py-3 text-center">Source</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading report...
                    </td>
                  </tr>
                ) : sortedJobs.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                      No jobs found for the selected date and filters
                    </td>
                  </tr>
                ) : (
                  sortedJobs.map((job, index) => (
                    <React.Fragment key={job.id}>
                      <tr className="border-t hover:bg-purple-50 transition">
                        <td className="px-4 py-3 font-bold text-purple-600">{index + 1}</td>
                        <td className="px-4 py-3 font-medium">{job.jobTitle || job.title}</td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold">{job.company.name}</p>
                            {!job.company.verified && (
                              <p className="text-xs text-gray-500">⚠️ Unverified</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">{job.industry}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-white font-medium ${
                            job.ada.complianceScore >= 80 ? 'bg-green-600' :
                            job.ada.complianceScore >= 60 ? 'bg-yellow-600' :
                            'bg-red-600'
                          }`}>
                            {job.ada.complianceScore}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                            {job.analytics.relevanceScore}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {job.compensation.salaryMin ? (
                            <span>${(job.compensation.salaryMin/1000).toFixed(0)}K - ${(job.compensation.salaryMax/1000).toFixed(0)}K</span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-xs">
                          <span className="inline-block px-2 py-1 rounded bg-gray-200 text-gray-800">
                            {job.posting.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {job.company.hrEmail && (
                            <p className="text-blue-600 hover:underline cursor-pointer">
                              {job.company.hrEmail}
                            </p>
                          )}
                          {job.company.phone && (
                            <p className="text-gray-600">{job.company.phone}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                          >
                            {expandedJobId === job.id ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {expandedJobId === job.id && (
                        <tr className="bg-purple-50 border-t">
                          <td colSpan="10" className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Left Column */}
                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">📋 Job Requirements</h4>
                                <div className="bg-white p-3 rounded border border-purple-200 text-sm space-y-2">
                                  <p><strong>Level:</strong> {job.level}</p>
                                  <p><strong>Experience:</strong> {job.requirements.yearsOfExperience} years</p>
                                  <p><strong>Technical Skills:</strong></p>
                                  <div className="flex flex-wrap gap-1">
                                    {job.requirements.technicalSkills.map((skill, i) => (
                                      <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                  <p><strong>Certifications:</strong> {job.requirements.certifications.join(', ') || 'None'}</p>
                                </div>

                                <h4 className="font-bold text-gray-900 mt-4 mb-3">💼 Compensation</h4>
                                <div className="bg-white p-3 rounded border border-purple-200 text-sm space-y-2">
                                  <p><strong>Salary:</strong> ${job.compensation.salaryMin?.toLocaleString()} - ${job.compensation.salaryMax?.toLocaleString()}</p>
                                  <p><strong>Benefits:</strong></p>
                                  <ul className="list-disc list-inside text-xs text-gray-700">
                                    {job.compensation.benefits.map((b, i) => (
                                      <li key={i}>{b}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {/* Right Column */}
                              <div>
                                <h4 className="font-bold text-gray-900 mb-3">♿ ADA & Accessibility</h4>
                                <div className="bg-white p-3 rounded border border-green-200 text-sm space-y-2">
                                  <p><strong>Remote Friendly:</strong> {job.ada.remoteFriendly ? '✅ Yes' : '❌ No'}</p>
                                  <p><strong>Flexible Schedule:</strong> ✅ Yes</p>
                                  <p><strong>Features:</strong></p>
                                  <ul className="list-disc list-inside text-xs text-gray-700">
                                    {job.ada.accessibilityFeatures.map((f, i) => (
                                      <li key={i}>{f}</li>
                                    ))}
                                  </ul>
                                </div>

                                <h4 className="font-bold text-gray-900 mt-4 mb-3">🎯 Sourcing Status</h4>
                                <div className="bg-white p-3 rounded border border-orange-200 text-sm space-y-2">
                                  <p><strong>Candidates Qualified:</strong> {job.sourcing.candidatesQualified}</p>
                                  <p><strong>Conversion Rate:</strong> {job.sourcing.conversionRate}%</p>
                                  <div className="mt-2">
                                    <button
                                      onClick={() => {
                                        const qualified = prompt('Enter number of qualified candidates:', job.sourcing.candidatesQualified);
                                        if (qualified !== null) {
                                          handleUpdateSourcing(job.id, {
                                            candidatesQualified: parseInt(qualified) || 0,
                                            candidatesApplied: job.sourcing.candidatesApplied || 1
                                          });
                                        }
                                      }}
                                      className="w-full px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                                    >
                                      Update Sourcing Data
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Action Links */}
                            <div className="mt-4 flex gap-2">
                              {job.posting.link && (
                                <a
                                  href={job.posting.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                >
                                  View Job Posting
                                </a>
                              )}
                              {job.company.hrEmail && (
                                <a
                                  href={`mailto:${job.company.hrEmail}`}
                                  className="px-3 py-2 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                                >
                                  Email HR
                                </a>
                              )}
                            </div>
                            
                            {/* Company Resources & AI Description */}
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                <h5 className="font-semibold mb-2">🏢 Company Resources</h5>
                                <p><strong>Website:</strong> {job.company.website ? (
                                  <a href={job.company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{job.company.website}</a>
                                ) : 'N/A'}</p>
                                <p><strong>LinkedIn:</strong> {job.company.linkedinPage ? (
                                  <a href={job.company.linkedinPage} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{job.company.linkedinPage}</a>
                                ) : 'N/A'}</p>
                                <p><strong>Hiring Manager:</strong> {job.company.hiringManager || 'N/A'}</p>
                                <p><strong>Phone:</strong> {job.company.phone || 'N/A'}</p>
                              </div>

                              <div className="bg-white p-3 rounded border border-gray-200 text-sm">
                                <h5 className="font-semibold mb-2">🤖 AI Generated Summary</h5>
                                <p className="text-sm text-gray-800">{job.aiGenerated?.description || job.aiGenerated?.summary || 'No AI description available.'}</p>
                                {job.aiGenerated?.keyResponsibilities && (
                                  <>
                                    <p className="mt-2 font-semibold">Key Responsibilities:</p>
                                    <ul className="list-disc list-inside text-xs text-gray-700">
                                      {job.aiGenerated.keyResponsibilities.map((r, i) => (
                                        <li key={i}>{r}</li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900">
            <strong>ℹ️ Top 25% Selection:</strong> Jobs are ranked by relevance score combining ADA compliance, company verification, and posting quality. Analysts can update sourcing data and track candidate conversions directly from this report.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalystDailyReport;
